import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { ROLE_LABELS } from "../lib/format";
import {
  LayoutDashboard, ShoppingCart, Package, Boxes, Truck, ReceiptText,
  Wallet, BookUser, FileBarChart, Users, ScrollText, DatabaseBackup,
  LogOut, Wifi, WifiOff, AlertTriangle, Store, Settings, PanelLeftClose, PanelLeftOpen, BadgeDollarSign,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const MENU = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["master", "admin", "gm"] },
  { to: "/pos", label: "Kasir (POS)", icon: ShoppingCart, roles: ["master", "admin"] },
  { to: "/items", label: "Master Barang", icon: Package, roles: ["master", "admin", "gm"] },
  { to: "/stock", label: "Stok & Opname", icon: Boxes, roles: ["master", "admin", "gm"] },
  { to: "/purchases", label: "Pembelian", icon: Truck, roles: ["master", "admin", "gm"] },
  { to: "/sales", label: "Riwayat Penjualan", icon: ReceiptText, roles: ["master"] },
  { to: "/finance", label: "Buku Kas", icon: Wallet, roles: ["master", "admin", "gm"] },
  { to: "/debts", label: "Buku Hutang", icon: BookUser, roles: ["master", "admin"] },
  { to: "/payroll", label: "Karyawan & Gaji", icon: BadgeDollarSign, roles: ["master", "admin"] },
  { to: "/reports", label: "Laporan & PDF", icon: FileBarChart, roles: ["master", "admin", "gm"] },
  { to: "/users", label: "Manajemen User", icon: Users, roles: ["master", "admin", "gm"] },
  { to: "/audit", label: "Log Audit", icon: ScrollText, roles: ["master", "admin", "gm"] },
  { to: "/backup", label: "Backup & Restore", icon: DatabaseBackup, roles: ["master", "admin"] },
  { to: "/settings", label: "Pengaturan Akun", icon: Settings, roles: ["master", "admin", "gm"] },
];

export function Layout({ children }) {
  const { user, logout, isViewOnly } = useAuth();
  const { online, setOnline, lowStock, balance } = useApp();
  const navigate = useNavigate();
  const menu = MENU.filter((m) => m.roles.includes(user.role));
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("pos_sidebar_collapsed") === "1");

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("pos_sidebar_collapsed", next ? "1" : "0");
  };
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-slate-100">
      {/* Sidebar */}
      <aside className={`${collapsed ? "w-20" : "w-64"} shrink-0 bg-slate-900 text-slate-300 flex flex-col fixed h-screen transition-[width] duration-200 z-40`} data-testid="sidebar">
        <div className={`px-4 py-5 border-b border-slate-800 flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}>
          <div className="h-10 w-10 rounded-xl bg-orange-500 flex items-center justify-center shrink-0">
            <Store className="h-5 w-5 text-white" />
          </div>
          {!collapsed && (
            <div className="leading-tight">
              <div className="text-white font-bold font-display text-sm">Sumber Harapan</div>
              <div className="text-[11px] text-slate-400">Toko Bangunan · Bitung</div>
            </div>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {menu.map((m) => (
            <NavLink key={m.to} to={m.to} data-testid={`nav-${m.to.slice(1)}`} title={collapsed ? m.label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${collapsed ? "justify-center" : ""} ${
                  isActive ? "bg-orange-500 text-white" : "hover:bg-slate-800 hover:text-white"
                }`}>
              <m.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{m.label}</span>}
              {!collapsed && m.to === "/items" && lowStock.length > 0 && (
                <span className="ml-auto bg-rose-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  {lowStock.length}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className={`flex items-center gap-3 px-2 py-2 ${collapsed ? "justify-center" : ""}`}>
            <div className="h-9 w-9 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-sm shrink-0">
              {user.name?.[0]?.toUpperCase()}
            </div>
            {!collapsed && (
              <div className="leading-tight min-w-0">
                <div className="text-white text-sm font-medium truncate">{user.name}</div>
                <div className="text-[11px] text-orange-400">{ROLE_LABELS[user.role]}</div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 ${collapsed ? "ml-20" : "ml-64"} flex flex-col min-h-screen transition-[margin] duration-200`}>
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={toggle} data-testid="sidebar-toggle"
              className="h-9 w-9 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 transition-colors">
              {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
            </button>
            {isViewOnly && (
              <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">
                Mode Lihat Saja (GM)
              </Badge>
            )}
            {balance.record && (
              <span className="hidden sm:inline text-xs text-slate-500">
                Saldo Awal Hari Ini: <span className="font-mono font-semibold text-slate-800">Rp {Number(balance.record.amount).toLocaleString("id-ID")}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {lowStock.length > 0 && (
              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 gap-1">
                <AlertTriangle className="h-3 w-3" /> {lowStock.length} stok menipis
              </Badge>
            )}
            <button onClick={() => setOnline(!online)} data-testid="online-toggle"
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                online ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"
              }`}>
              {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
              {online ? "Online" : "Offline"}
            </button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="hidden sm:flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-slate-800 flex items-center justify-center text-white font-bold text-xs">
                {user.name?.[0]?.toUpperCase()}
              </div>
              <span className="text-sm font-medium text-slate-700 max-w-[120px] truncate">{user.name}</span>
            </div>
            <Button onClick={doLogout} variant="outline" size="sm" data-testid="logout-button"
              className="text-slate-600 hover:text-rose-600 hover:border-rose-300">
              <LogOut className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Keluar</span>
            </Button>
          </div>
        </header>
        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
}
